import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import classNames from 'classnames';
import { canUseDOM, trackEvent } from '@surfline/web-common';
import { WHATS_NEW_URL } from 'common/constants';

import config from 'config';
import CloudflareResizedImage from 'components/CloudflareResizedImage';
import ErrorBoundary from 'components/ErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import { Facebook, Instagram, SurflineLogo, TikTok, Twitter, YouTube } from 'components/Icons';
import { OneTrustLink } from 'components/OneTrust';
import WavetrakLink from 'components/WavetrakLink';
import { getIsSubscribed } from 'selectors/user';
import { useAppSelector } from 'stores/hooks';

// @ts-ignore: Missing SVG module declaration
import wslLogo from '../../../public/images/wsl-logo.svg';
// @ts-ignore: Missing SVG module declaration
import elSalvadorLogo from '../../../public/images/el-salvador-logo.svg';
// @ts-ignore: Missing SVG module declaration
import jsIndustriesLogo from '../../../public/images/js-industries-logo.svg';
// @ts-ignore: Missing SVG module declaration
import sunBumLogo from '../../../public/images/sunbum-logo-bw.svg';
// @ts-ignore: Missing SVG module declaration
import vansLogo from '../../../public/images/vans-logo.svg';

import styles from './Footer.module.scss';

interface FooterLinkProps {
  href: string;
  isExternal?: boolean;
  text: string;
}

const handleClick = ({ href, text }: FooterLinkProps) =>
  trackEvent('Clicked Link', {
    linkLocation: 'footer',
    linkUrl: href,
    linkName: text,
  });

const FooterLink = ({ href, isExternal = true, text }: FooterLinkProps) => (
  <WavetrakLink
    href={href}
    isExternal={isExternal}
    onClick={() => handleClick({ href, text })}
    prefetch={!isExternal ? false : undefined}
  >
    <Typography variant="body1" component="span">
      {text}
    </Typography>
  </WavetrakLink>
);

const trackLogoClick = (partnerName: string) => {
  trackEvent('Button Clicked', {
    buttonText: partnerName,
    uiElement: 'partner logo',
    pageName: 'footer',
  });
};

const trackJSIndustriesLinkClick = () => trackLogoClick('JS Industries');
const handleVansLinkClick = () => trackLogoClick('Vans');
const handleWSLinkClick = () => trackLogoClick('WSL');
const handleElSalvadorLinkClick = () => trackLogoClick('El Salvador');
const handleSunBumLinkClick = () => trackLogoClick('Sun Bum');

interface FooterContentProps {
  isSmall?: boolean;
  isVisible?: boolean;
}

const FooterContent: React.FC<FooterContentProps> = ({ isSmall = false, isVisible = true }) => {
  const footerClassNames = classNames({
    [styles.container]: true,
    [styles.containerSmall]: isSmall,
  });

  const handleConsentManagerButtonClick = () =>
    trackEvent('Clicked Link', {
      linkLocation: 'footer',
      linkName: 'Do Not Sell My Personal Information',
    });

  const theme = useTheme();
  const isSubscribed = useAppSelector(getIsSubscribed);

  return (
    <footer
      className={classNames(styles.footer, { [styles.footerHidden]: !isVisible })}
      data-testid="footer-content"
    >
      <div className="sl-section-container">
        <div className={footerClassNames}>
          <ul className={styles.brand}>
            <li className={styles.copyrightLogo}>
              <SurflineLogo fill={theme.palette.secondary.main} />
            </li>
            <li>
              <Typography
                variant="body1"
                component="span"
              >{`©${new Date().getFullYear()} Surfline\\Wavetrak, Inc.`}</Typography>
            </li>
            <li>
              <FooterLink href="/terms-of-use" isExternal={false} text="Terms of Use" />
              <Typography variant="body1" component="span">
                {' '}
                and{' '}
              </Typography>
              <FooterLink href="/privacy-policy" isExternal={false} text="Privacy Policy" />.
            </li>
            <li>
              <OneTrustLink onClick={() => canUseDOM && handleConsentManagerButtonClick()} />
            </li>
            <li>
              <ul className={styles.socialLogos}>
                <li>
                  <a
                    href="https://www.facebook.com/Surfline/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Facebook />
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.instagram.com/surfline/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Instagram />
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.youtube.com/surfline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <YouTube />
                  </a>
                </li>
                <li>
                  <a href="https://twitter.com/surfline" target="_blank" rel="noopener noreferrer">
                    <Twitter />
                  </a>
                </li>
                <li>
                  <a
                    href="https://tiktok.com/@surfline_official"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <TikTok />
                  </a>
                </li>
              </ul>
            </li>
          </ul>
          <ul className={styles.linkGroup}>
            <li>
              <Typography variant="callout1" component="h2" className={styles.heading}>
                Company
              </Typography>
            </li>
            <li>
              <FooterLink href="https://careers.surfline.com" text="Careers" />
            </li>
            <li>
              <FooterLink href={WHATS_NEW_URL} text="What's New" />
            </li>
            <li>
              <FooterLink href="https://go.surfline.com/ads" text="Advertise" />
            </li>
            <li>
              <FooterLink href="https://go.surfline.com/cam-host" text="Host a Camera" />
            </li>
          </ul>
          <ul className={styles.linkGroup}>
            <li>
              <Typography variant="callout1" component="h2" className={styles.heading}>
                Support
              </Typography>
            </li>
            <li>
              <FooterLink href="https://support.surfline.com" text="Support Center" />
            </li>
            <li>
              <FooterLink href={`${config.surflineHost}/account`} text="My Account" />
            </li>
            {!isSubscribed && (
              <li>
                <FooterLink href={`${config.surflineHost}/upgrade`} text="Get Premium" />
              </li>
            )}
            <li>
              <FooterLink href={`${config.surflineHost}/gift-cards`} text="Gift Cards" />
            </li>
          </ul>
          <ul className={styles.linkGroup}>
            <li>
              <Typography variant="callout1" component="h2" className={styles.heading}>
                Partners
              </Typography>
            </li>
            <li className={styles.partnerLogos}>
              <a
                href="https://bit.ly/wslsurfline"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleWSLinkClick}
              >
                <CloudflareResizedImage src={wslLogo} alt="WSL Logo" width={100} height={32} />
              </a>
              <a
                href="https://bit.ly/elsalvadorsurfline"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleElSalvadorLinkClick}
              >
                <CloudflareResizedImage
                  src={elSalvadorLogo}
                  alt="Surf City El Salvador Logo"
                  width={120}
                  height={32}
                />
              </a>
              <a
                href="https://bit.ly/3QqJKIr"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleSunBumLinkClick}
              >
                <CloudflareResizedImage
                  alt="Sun Bum Logo"
                  src={sunBumLogo}
                  width={120}
                  height={32}
                />
              </a>
              <a
                href="https://bit.ly/vanssurfline"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleVansLinkClick}
              >
                <CloudflareResizedImage src={vansLogo} alt="Vans Logo" width={100} height={30} />
              </a>
              <a
                href="https://bit.ly/jssurflineusa"
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackJSIndustriesLinkClick}
              >
                <CloudflareResizedImage
                  alt="JS Industries Logo"
                  src={jsIndustriesLogo}
                  width={170}
                  height={32}
                />
              </a>
            </li>
          </ul>
        </div>
      </div>
    </footer>
  );
};

export interface FooterProps extends FooterContentProps {
  isError?: boolean;
}

export const Footer: React.FC<FooterProps> = (props) => {
  const { isError } = props;
  return (
    <div data-testid="footer">
      <ErrorBoundary
        error={isError}
        errorBoundaryId="footer"
        render={() => (
          <Box className={styles.footerError} data-testid="footer-error">
            <ErrorMessage dataTestId="error-message" />
          </Box>
        )}
      >
        <FooterContent {...props} />
      </ErrorBoundary>
    </div>
  );
};

export default Footer;
